import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";
import { SwipeableDrawer } from "@mui/material";

import MenuItems from "../MenuItems/MenuItems";
import * as NavigationUtils from "../../../lib/util/NavigationUtils";
import * as SupportUtils from "../../../lib/util/SupportUtils";
import * as Utils from "../../../lib/util/Utils";
import { IconClose } from "../../../assets";
import Logo from "../../custom/Logo/Logo";
import { getUserDisplayName } from "../../../lib/util/FormatUtils";
import * as GlobalSelectors from "../../../store/Global/Global.selector";
import * as UserSelectors from "../../../store/User/User.selector";
import * as FleetSelectors from "../../../store/Fleet/Fleet.selector";
import * as ScopeSelectors from "../../../store/Scope/ScopeSelectors";
import * as GlobalActions from "../../../store/Global/Global.action";
import * as UserActions from "../../../store/User/User.action";
import IconButton from "../../common/IconButton/IconButton";
import DebugArea from "../../common/DebugArea/DebugArea";

import routes from "./routes.json";
import { useDispatch, useSelector } from "../../../lib/hook/ReduxHooks";
import { MenuItem } from "../MenuDropdown/MenuDropdown";
import { TrackingContext } from "../../../lib/util/AnalyticsUtils";
import { filterMenuItems } from "../Navigation/Navigation";

const MobileNav = () => {
	const [selectedTopRoute, setSelectedTopRoute] = useState<string | null>(
		routes.top[0].id,
	);
	const [selectedBottomRoute, setSelectedBottomRoute] = useState<string | null>(
		null,
	);

	const dispatch = useDispatch();

	const { open, user, fleet, group, groups, driverless, demoMode } =
		useSelector((state: any) => ({
			open: GlobalSelectors.isMobileNavVisible(state),
			user: UserSelectors.selectLoggedInUser(state),
			fleet: UserSelectors.selectCurrentScopeFleet(state),
			group: UserSelectors.selectCurrentScopeGroup(state),
			groups: FleetSelectors.selectCurrentScopeFleetGroups(state),
			driverless: ScopeSelectors.selectIsCurrentScopeDriverless(state),
			demoMode: GlobalSelectors.isDemoModeEnabled(state),
		}));
	const openRef = useRef(open);
	openRef.current = open;

	useEffect(() => {
		NavigationUtils.onRouteChange(() => {
			if (openRef.current) {
				dispatch(GlobalActions.setMobileNavVisible(false));
			}
		});
	}, [dispatch]);

	const handleMenuItemClick = useCallback(
		(route: MenuItem) => {
			// signout is handled through the middleware
			if (route.id === "signOut") {
				dispatch(UserActions.signOut());
			} else if (route.id === "debug") {
				DebugArea.toggle();
			} else if (route.id === "contact") {
				SupportUtils.contactSupport();
			}
			if (route.id === "faq") {
				SupportUtils.openFAQ();
			}

			if (routes["bottom"].some((r) => r.id === route.id)) {
				setSelectedBottomRoute(route.id);
				setSelectedTopRoute(null);
			} else {
				setSelectedTopRoute(route.id);
				setSelectedBottomRoute(null);
			}

			dispatch(GlobalActions.setMobileNavVisible(false));
		},
		[dispatch],
	);

	const handleCloseMobileNav = useCallback(() => {
		dispatch(GlobalActions.setMobileNavVisible(false));
	}, [dispatch]);

	// remap the Route json to show correct user values`
	const userName = user
		? getUserDisplayName(user.firstName, user.lastName, user.uid, false)
		: "Profile";
	(routes.bottom.find((route) => route.id === "profile") as any).displayName =
		userName;

	const classes = clsx(open && "--visible");

	const handleFilter = useCallback(
		(item: MenuItem) =>
			filterMenuItems(
				item,
				user,
				driverless,
				demoMode,
				fleet.value,
				group,
				groups.value,
			),
		[user, fleet.value, group, groups.value, driverless, demoMode],
	);

	const menuItemsTop = (
		<MenuItems
			selected={selectedTopRoute}
			routes={routes.top || []}
			onFilterItem={handleFilter}
			className={clsx(classes, "--mobile-nav-top")}
			onSelect={handleMenuItemClick}
		/>
	);

	const menuItemsBottom = (
		<MenuItems
			selected={selectedBottomRoute}
			routes={routes.bottom || []}
			className={clsx(classes, "--mobile-nav-bottom")}
			onSelect={handleMenuItemClick}
		/>
	);

	const navHeader = (
		<div className={"MobileNav__header"}>
			<Logo className={"MobileNav__logo"} onClick={handleCloseMobileNav} />
			<div>
				<IconButton
					onClick={handleCloseMobileNav}
					className={"MobileNav__toggle-container"}
				>
					<IconClose />
				</IconButton>
			</div>
		</div>
	);

	return (
		<SwipeableDrawer
			onOpen={Utils.noop}
			open={open}
			classes={{ paper: "MobileNav__container" }}
			onClose={handleCloseMobileNav}
			className={clsx("MobileNav", classes)}
		>
			<TrackingContext.Provider value={"Mobile Navigation"}>
				{navHeader}
				{menuItemsTop}
				<div className={"MobileNav__divider"} />
				{menuItemsBottom}
			</TrackingContext.Provider>
		</SwipeableDrawer>
	);
};

export default React.memo(MobileNav);
